// MUS-449 - shared MIDI byte parser.
//
// The web (Web MIDI `onmidimessage`) and native (`MIDIReceived` bridge) input paths
// both need to turn raw MIDI bytes into channel-voice messages. This is the single
// pure implementation both use. It honors running status, skips real-time bytes
// between messages, and drops system common messages (sysex, song position, ...),
// which is exactly what a Bluetooth (BLE) MIDI keyboard delivers - so testing this
// function proves the note path for BLE input with no hardware.

export interface MidiChannelMessage {
  /** Full status byte (command | channel), e.g. 0x90 = Note On ch 1. */
  status: number;
  data1: number;
  data2: number;
}

export interface MidiParseResult {
  messages: MidiChannelMessage[];
  /** Running status to carry into the next packet (null if none established). */
  runningStatus: number | null;
}

const REALTIME_MIN = 0xf8; // F8..FF: system real-time (clock, active sensing, transport)
const isRealtime = (b: number): boolean => b >= REALTIME_MIN;
// F0..F7: system common (sysex start and end, song position, song select, tune request)
const isSystemCommon = (b: number): boolean => b >= 0xf0 && b < REALTIME_MIN;
const isDataByte = (b: number): boolean => b < 0x80;
const isStatus = (b: number): boolean => b >= 0x80 && b < 0xf0; // channel-voice status only
const msgDataLen = (status: number): number => {
  const hi = status & 0xf0;
  // Program Change (Cn) and Channel Pressure (Dn) carry 1 data byte; the rest carry 2.
  return hi === 0xc0 || hi === 0xd0 ? 1 : 2;
};

/**
 * Parse one transport packet of raw MIDI bytes into complete channel-voice messages.
 *
 * Pure and deterministic: pass the running status carried in from the previous packet
 * (or null), receive the parsed messages plus the running status to carry forward. A
 * trailing incomplete message is left unparsed and its status is carried out so the
 * next packet can continue - matching how the native bridge behaves across callbacks.
 */
export function parseMidiStream(
  bytes: readonly number[],
  runningStatusIn: number | null = null,
): MidiParseResult {
  const messages: MidiChannelMessage[] = [];
  let runningStatus = runningStatusIn;

  // A lone real-time byte carries no channel data.
  if (bytes.length === 1 && isRealtime(bytes[0])) {
    return { messages, runningStatus };
  }

  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i];

    // A real-time byte between messages carries no channel data; skip it.
    if (isRealtime(b)) {
      i += 1;
      continue;
    }

    // A system common message ends running status. Skip it together with its data
    // bytes (a sysex payload, song position's two bytes) up to the next status byte,
    // so none of them is read as a note under the old running status.
    if (isSystemCommon(b)) {
      runningStatus = null;
      i += 1;
      while (i < bytes.length && isDataByte(bytes[i])) i += 1;
      continue;
    }

    // A new status byte (re)establishes running status.
    if (isStatus(b)) {
      runningStatus = b;
      i += 1;
      const needed = msgDataLen(runningStatus);
      if (i + needed > bytes.length) break; // incomplete; carry runningStatus onward
      const d1 = bytes[i];
      const d2 = needed === 2 ? bytes[i + 1] : 0;
      messages.push({ status: runningStatus, data1: d1, data2: d2 });
      i += needed;
      continue;
    }

    // A data byte under an established running status.
    if (runningStatus !== null) {
      const needed = msgDataLen(runningStatus);
      if (needed === 2 && i + 1 >= bytes.length) break; // incomplete
      const d1 = bytes[i];
      const d2 = needed === 2 ? bytes[i + 1] : 0;
      messages.push({ status: runningStatus, data1: d1, data2: d2 });
      i += needed;
      continue;
    }

    // Stray data byte with no running status: skip it.
    i += 1;
  }

  return { messages, runningStatus };
}
